import type { Database } from "@/integrations/supabase/types";
import { amenityByKey, type AmenitySpec } from "@/lib/amenities";
import {
  countActiveFilters,
  parseBrowseSearch,
  pickBrowseParams,
  type BrowseContext,
  type BrowseSearch,
} from "@/lib/browseFilters";
import { interpolateCopy, type CopyVars } from "@/lib/siteCopy";
import { parseThingsToDo, thingsToDoToJson, type ThingToDo } from "@/lib/thingsToDo";

// A collection is a saved browse search plus copy: the "intent × market"
// landing pages (pet-friendly monthly rentals, homes with a pool, walk to
// the beach…). Rows live in `public.collections`, per city (tenant_id) or for
// every site (tenant_id NULL); a city row with the same slug overrides the
// global one. Pure helpers only, the hook and the API module do the I/O.

export type CollectionRow = Database["public"]["Tables"]["collections"]["Row"];
export type CollectionUpsert = Database["public"]["Tables"]["collections"]["Insert"];

export interface FaqItem {
  q: string;
  a: string;
}

export interface Collection {
  id: string;
  /** null = every city (a global row). */
  tenantId: string | null;
  slug: string;
  title: string;
  intro: string;
  /** Browse query string (src/lib/browseFilters.ts grammar). */
  filter: string;
  /** Amenity registry keys shown as "the standard" for this intent. */
  amenityStandard: string[];
  faq: FaqItem[];
  thingsToDo: ThingToDo[];
  seoTitle: string;
  seoDescription: string;
  published: boolean;
  sortOrder: number;
  updatedAt: string;
}

export const COLLECTION_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const parseFaq = (value: unknown): FaqItem[] => {
  if (!Array.isArray(value)) return [];
  const out: FaqItem[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    const q = typeof r.q === "string" ? r.q.trim() : "";
    const a = typeof r.a === "string" ? r.a.trim() : "";
    if (q && a) out.push({ q, a });
  }
  return out;
};

export const rowToCollection = (row: CollectionRow): Collection => ({
  id: row.id,
  tenantId: row.tenant_id,
  slug: row.slug,
  title: row.title,
  intro: row.intro ?? "",
  filter: row.filter ?? "",
  amenityStandard: (row.amenity_standard ?? []).filter((k) => !!amenityByKey(k)),
  faq: parseFaq(row.faq),
  thingsToDo: parseThingsToDo(row.things_to_do),
  seoTitle: row.seo_title ?? "",
  seoDescription: row.seo_description ?? "",
  published: row.published,
  sortOrder: row.sort_order,
  updatedAt: row.updated_at,
});

const nul = (s: string) => (s.trim() ? s.trim() : null);

/** The columns an admin save writes (empty strings become NULL). */
export const collectionToRow = (c: Omit<Collection, "id" | "updatedAt">, updatedBy: string | null): CollectionUpsert => ({
  tenant_id: c.tenantId,
  slug: c.slug.trim(),
  title: c.title.trim(),
  intro: nul(c.intro),
  filter: pickBrowseParams(new URLSearchParams(c.filter.trim().replace(/^\?/, ""))).toString(),
  amenity_standard: c.amenityStandard.filter((k) => !!amenityByKey(k)),
  faq: c.faq.filter((f) => f.q.trim() && f.a.trim()).map((f) => ({ q: f.q.trim(), a: f.a.trim() })),
  things_to_do: thingsToDoToJson(c.thingsToDo),
  seo_title: nul(c.seoTitle),
  seo_description: nul(c.seoDescription),
  published: c.published,
  sort_order: c.sortOrder,
  updated_by: updatedBy,
});

/**
 * The collections a site shows: its own rows override global rows with the
 * same slug; published only unless asked; ordered by sort_order, then title.
 */
export function resolveCollections(
  rows: readonly CollectionRow[],
  tenantId: string,
  opts: { includeUnpublished?: boolean } = {},
): Collection[] {
  const bySlug = new Map<string, CollectionRow>();
  for (const row of rows) {
    if (row.tenant_id !== null && row.tenant_id !== tenantId) continue;
    const current = bySlug.get(row.slug);
    if (!current || (current.tenant_id === null && row.tenant_id === tenantId)) bySlug.set(row.slug, row);
  }
  return [...bySlug.values()]
    .filter((r) => opts.includeUnpublished || r.published)
    .map(rowToCollection)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
}

export const collectionPath = (slug: string, areaSlug?: string | null): string =>
  `/collections/${slug}${areaSlug ? `/${areaSlug}` : ""}`;

/** The saved filter, with the area child narrowing it. */
export const collectionSearchParams = (c: Pick<Collection, "filter">, areaSlug?: string | null): URLSearchParams => {
  const p = new URLSearchParams(c.filter);
  if (areaSlug) p.set("area", areaSlug);
  return p;
};

export const collectionSearch = (c: Pick<Collection, "filter">, ctx: BrowseContext, areaSlug?: string | null): BrowseSearch =>
  parseBrowseSearch(collectionSearchParams(c, areaSlug), ctx);

/** The browse page showing the same homes ("See all in browse"). */
export const collectionBrowseHref = (c: Pick<Collection, "filter">, areaSlug?: string | null): string => {
  const qs = pickBrowseParams(collectionSearchParams(c, areaSlug)).toString();
  return `/properties${qs ? `?${qs}` : ""}`;
};

export interface CollectionCopy {
  title: string;
  intro: string;
  seoTitle: string;
  seoDescription: string;
}

/**
 * Copy with the site's placeholders filled in. On an area child, {city}
 * becomes the area's name so "Pet-friendly rentals in {city}" reads
 * "…in Playa Hermosa".
 */
export const collectionCopy = (
  c: Pick<Collection, "title" | "intro" | "seoTitle" | "seoDescription">,
  vars: Partial<CopyVars>,
  area?: { name: string } | null,
): CollectionCopy => {
  const v = area ? { ...vars, city: area.name } : vars;
  const title = interpolateCopy(c.title, v);
  return {
    title,
    intro: interpolateCopy(c.intro, v),
    seoTitle: interpolateCopy(c.seoTitle, v) || `${title} | ${vars.siteName ?? ""}`.trim(),
    seoDescription: interpolateCopy(c.seoDescription, v) || interpolateCopy(c.intro, v).slice(0, 300),
  };
};

export const amenityStandardSpecs = (c: Pick<Collection, "amenityStandard">): AmenitySpec[] =>
  c.amenityStandard.map((k) => amenityByKey(k)).filter((s): s is AmenitySpec => !!s);

/**
 * The single-facet collection for an amenity label, the one a listing's
 * amenity chip links to ("More homes with a dishwasher in Santa Teresa").
 */
export function collectionForAmenity(
  collections: readonly Collection[],
  amenityLabel: string,
  ctx: BrowseContext,
): Collection | undefined {
  return collections.find((c) => {
    const s = collectionSearch(c, ctx);
    return countActiveFilters(s, ctx) === 1 && s.selectedAmenities.length === 1 && s.selectedAmenities[0] === amenityLabel;
  });
}

/** A collection's filter as chips ("Pool", "3+ bedrooms", "≤ 10 min to the beach"). */
export function describeSearch(s: BrowseSearch, ctx: BrowseContext): string[] {
  const out: string[] = [];
  for (const l of s.locations) out.push(l);
  if (s.bedrooms !== "any") out.push(`${s.bedrooms}+ bedrooms`);
  if (s.bathrooms !== "any") out.push(`${s.bathrooms}+ bathrooms`);
  if (s.type !== "any") out.push(s.type.charAt(0).toUpperCase() + s.type.slice(1));
  const [lo, hi] = s.priceRange;
  if (lo > ctx.priceMin && hi < ctx.priceMax) out.push(`$${lo.toLocaleString()}–$${hi.toLocaleString()}/mo`);
  else if (lo > ctx.priceMin) out.push(`From $${lo.toLocaleString()}/mo`);
  else if (hi < ctx.priceMax) out.push(`Up to $${hi.toLocaleString()}/mo`);
  for (const a of s.selectedAmenities) out.push(a);
  for (const sv of s.selectedServices) out.push(`${sv} included`);
  if (s.walkMax != null) out.push(`≤ ${s.walkMax} min walk to the beach`);
  if (s.stayMonths != null) out.push(`Stays of ${s.stayMonths}+ months`);
  return out;
}
